/* eslint-disable no-bitwise */
import { HandType } from '@ps/web-poker-types';
import { Currency } from '@ps/web-poker-utils';

import type { getStakeProps } from '../types';

import { NG_CDN_IMAGE_SUFFIX, NG_CDN_WIDE_IMAGE_SUFFIX, Structure } from './game';
import { TournGroupName } from './tournGroups';
import { NGSAGFlagTypes, eSpinAndGoPublicationFlags } from './types';

const isNumber = (value: unknown): value is number => typeof value === 'number';

export const getBuyInCategory = (buyIn: number, ranges: (string | number)[]) => {
  const buyInRanges = [0, ...ranges, Infinity];

  for (let i = 0; i < buyInRanges.length; i += 2) {
    const current = buyInRanges[i];
    const next = buyInRanges[i + 2];

    if (isNumber(current) && isNumber(next) && buyIn >= current && buyIn < next) {
      return buyInRanges[i + 1];
    }
  }

  throw new Error('Something is wrong with the ranges');
};

export const getTournImgStr = (tournName = '') =>
  tournName.replace(/-/g, '_').replace(/ /g, '_').replace(/'/g, '').toLowerCase();

export const getTournGroupStr = (type: number, isThemeV2Image?: boolean) => {
  const tournName = TournGroupName[type];
  // NG CDN have a suffix of _square POKER-12433
  // MTT/SM lobby need _wide, Rail themeV2 use square POKER-13871
  return `${getTournImgStr(tournName)}${
    isThemeV2Image ? NG_CDN_IMAGE_SUFFIX : NG_CDN_WIDE_IMAGE_SUFFIX
  }`;
};

export const isLimitGame = (structure: number): boolean => structure === Structure.L;

export const isNGSAGFlash = (flags: number) => Boolean(flags & NGSAGFlagTypes.NGSAGFlag_SAGFlash);

export const isSAGFlashVariant = (flags: number) =>
  Boolean(flags & eSpinAndGoPublicationFlags.espfVariants);

export const isSAGMax = (flags: number) => Boolean(flags & eSpinAndGoPublicationFlags.espfSagMax);

export const isSAGHoldem = (game: number) => HandType[game] === HandType[HandType.HOLDEM];

export const isSAGHoldemSix = (
  game: number,
  flags: number,
  mods: {
    numUsers: number;
  }[]
) => {
  if (!flags || !mods?.length) return false;

  const numUsers = mods?.[0]?.numUsers;
  // Check for flags is if it is only 'fixed' or 'fixed' and 'promo'
  return (
    isSAGHoldem(game) &&
    (flags === eSpinAndGoPublicationFlags.espfFixedPayout ||
      flags ===
        eSpinAndGoPublicationFlags.espfFixedPayout + eSpinAndGoPublicationFlags.espfPromoMode) &&
    numUsers === 6
  );
};

export const isSwapShowFusion = (game: number): boolean =>
  HandType[game] === HandType[HandType.SWAP_HOLDEM] ||
  HandType[game] === HandType[HandType.SHOWTIME_HOLDEM] ||
  HandType[game] === HandType[HandType.FUSION] ||
  HandType[game] === HandType[HandType.SPLIT_HOLDEM];

export enum GameStake {
  // 25-99.99
  HIGH = 'stake-high', // 0.25 - 5
  LOW = 'stake-low', // 5.01 - 24.99
  MEDIUM = 'stake-medium',
  MICRO = 'stake-micro', // 100+
  ULTRA = 'stake-ultra' // 1M promo
}

export const K = 1000;
export const CENTS_K = K * 100;
export const CENTS_M = K * K * 100;

const getPlayMoneyStake = (value: number) => {
  if (value < 100 * CENTS_K) return GameStake.MICRO;
  if (value < CENTS_M) return GameStake.LOW;
  if (value < 50 * CENTS_M) return GameStake.MEDIUM;
  return GameStake.HIGH;
};

const getRealMoneyStake = (value: number) => {
  if (value <= 500) return GameStake.MICRO;
  if (value < 5000) return GameStake.LOW;
  if (value < 50000) return GameStake.MEDIUM;
  return GameStake.HIGH;
};

export const getStake = ({
  buyIn,
  currency,
  maxWinnerPayout,
  promoRef,
  rake,
  scalePM,
  isPlayMoney
}: getStakeProps) => {
  const baseStake = (buyIn + rake) * scalePM;

  if (isPlayMoney) {
    return getPlayMoneyStake(baseStake);
  }

  if (promoRef && maxWinnerPayout >= CENTS_M) {
    return GameStake.ULTRA;
  }

  const currencyStr = Currency[currency] || Currency[Currency.USD];

  // POKER-4903 Handle Indian rupee
  // Micro: 50, Low: < 500, Medium: < 5000
  // ULTRA: promoRef and the Top Prize is >= 10M (Rs 1 Cr)
  const currencyMultiplier = currencyStr === 'INR' ? 0.1 : 1;
  const adjustedValue = baseStake * currencyMultiplier;

  return getRealMoneyStake(adjustedValue);
};
